import type { ChangeEvent, FormEvent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Stack } from '@mui/material';

import useAuth from "../../hooks/useAuth";

const INPUT_SX = {
    height: 44, boxSizing: 'border-box', border: '1px solid #CFD6DE', borderRadius: '10px', px: 1.75, font: 'inherit', fontSize: 14,
    color: '#161C24', bgcolor: '#FFFFFF', width: '100%',
    '&:focus': { outline: '2px solid #2065D1', outlineOffset: 1 },
    '&[aria-invalid="true"]': { borderColor: '#C4262E' },
} as const;

const LABEL_SX = { fontSize: 13, fontWeight: 700 } as const;
const HELPER_SX = { fontSize: 12, color: '#C4262E' } as const;

export default function LoginForm() {
    const navigate = useNavigate();
    const auth = useAuth();

    const [ username, setUsername ] = useState('');
    const [ password, setPassword ] = useState('');
    const [ showPassword, setShowPassword ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ missing, setMissing ] = useState<{ username?: boolean; password?: boolean }>({});
    const [ error, setError ] = useState<string | null>(null);

    function handleSuccess() {
        let target = sessionStorage.getItem('login-redirect') || '/dashboard';
        if (target === '/login') target = '/dashboard';
        sessionStorage.removeItem('login-redirect');
        navigate(target, { replace: true });
    }

    function handleFailure(message: string) {
        setError(message || "Oops! Something went wrong. Please try again.");
        setLoading(false);
    }

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const nextMissing = { username: !username, password: !password };
        setMissing(nextMissing);
        if (nextMissing.username || nextMissing.password) return;

        setError(null);
        setLoading(true);
        auth.login({ username, password }, handleSuccess, handleFailure);
    }

    return (
        <Stack component="form" spacing={ 1.75 } noValidate onSubmit={ handleSubmit }>
            { error && <Alert severity="error"><b>Failure:</b> { error }</Alert> }

            <Stack spacing={ 0.75 }>
                <Box component="label" htmlFor="login-username" sx={ LABEL_SX }>Username</Box>
                <Box component="input" id="login-username" name="username" autoComplete="username" autoFocus value={ username }
                     aria-invalid={ !!missing.username } aria-describedby={ missing.username ? 'login-username-error' : undefined }
                     onChange={ (event: ChangeEvent<HTMLInputElement>) => setUsername(event.target.value) } sx={ INPUT_SX }/>
                { missing.username && <Box id="login-username-error" sx={ HELPER_SX }>Please enter a username</Box> }
            </Stack>

            <Stack spacing={ 0.75 }>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Box component="label" htmlFor="login-password" sx={ LABEL_SX }>Password</Box>
                    <Box component="button" type="button" onClick={ () => setShowPassword(!showPassword) } aria-controls="login-password" sx={ {
                        border: 0, p: 0, bgcolor: 'transparent', font: 'inherit', fontSize: 13, fontWeight: 700, color: '#1A55B3', cursor: 'pointer',
                    } }>
                        { showPassword ? 'Hide' : 'Show' }
                    </Box>
                </Stack>
                <Box component="input" id="login-password" name="password" type={ showPassword ? 'text' : 'password' } autoComplete="current-password"
                     value={ password } aria-invalid={ !!missing.password } aria-describedby={ missing.password ? 'login-password-error' : undefined }
                     onChange={ (event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value) } sx={ INPUT_SX }/>
                { missing.password && <Box id="login-password-error" sx={ HELPER_SX }>Please enter a password</Box> }
            </Stack>

            <Box component="button" type="submit" disabled={ loading } sx={ {
                height: 48, borderRadius: '12px', border: '1px solid #2065D1', bgcolor: '#2065D1', color: '#FFFFFF', font: 'inherit',
                fontSize: 15, fontWeight: 700, cursor: 'pointer', mt: '6px !important',
                '&:disabled': { opacity: 0.7, cursor: 'progress' },
                '&:focus-visible': { outline: '2px solid #2065D1', outlineOffset: 2 },
            } }>
                { loading ? 'Signing in…' : 'Sign in' }
            </Box>
        </Stack>
    );
}
